import React, { useEffect, useState, useMemo } from "react";
import {
  Card, Col, DatePicker, Row, Space, Spin, Table, Typography, Empty, message,
} from "antd";
import { BarChartOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";
import axios from "axios";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const API_URL = process.env.REACT_APP_API_URL;
const DATE_FMT = "YYYY-MM-DD";

export default function FrmLecheEstadisticas() {
  const [rango, setRango] = useState([dayjs().subtract(7, "day"), dayjs()]);
  const [cargando, setCargando] = useState(false);
  const [datos, setDatos] = useState([]); // LecheEstadisticaDto[]

  // Normaliza un posible { $values: [...] } a array plano
  const toArr = (v) =>
    Array.isArray(v?.$values) ? v.$values : Array.isArray(v) ? v : [];

  const cargar = async () => {
    if (!rango || rango.length !== 2) return;
    setCargando(true);
    try {
      const [desde, hasta] = rango;
      const resp = await axios.get(`${API_URL}/Leche/estadisticas`, {
        params: {
          desde: desde.format(DATE_FMT),
          hasta: hasta.format(DATE_FMT),
        },
      });
      // El backend usa ReferenceHandler.Preserve → los arrays llegan como { $values: [...] }
      const rawLista = toArr(resp.data);
      // Normalizar también el sub-array 'vacas' dentro de cada estadística
      const lista = rawLista.map(d => ({ ...d, vacas: toArr(d.vacas) }));
      setDatos(lista);
    } catch (err) {
      console.error("Error cargando estadísticas de leche:", err);
      message.error("Error al cargar las estadísticas de leche.");
      setDatos([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rango]);

  // ─── Resumen numérico ───────────────────────────────────────
  const totalLitros = useMemo(() => datos.reduce((s, d) => s + d.totalLitros, 0), [datos]);
  const totalVacasUnicas = useMemo(() => {
    const ids = new Set();
    datos.forEach(d => toArr(d.vacas).forEach(v => ids.add(v.animalId)));
    return ids.size;
  }, [datos]);
  const diasConDatos = datos.filter(d => d.cantidadVacas > 0).length;
  const promedioDiario = diasConDatos > 0 ? totalLitros / diasConDatos : 0;

  // ─── Datos para el gráfico ──────────────────────────────────
  const precioActual = datos.length > 0 ? Number(datos[0].precioPorLitro ?? 0) : 0;
  const totalIngresos = useMemo(
    () => datos.reduce((s, d) => s + Number(d.ingresoTotal ?? 0), 0),
    [datos],
  );

  const datosGrafico = useMemo(
    () =>
      datos.map(d => ({
        fecha: dayjs(d.fecha).format("DD/MM"),
        totalLitros: Number(d.totalLitros ?? 0),
        ingresoTotal: Number(d.ingresoTotal ?? 0),
        cantidadVacas: d.cantidadVacas ?? 0,
      })),
    [datos],
  );

  // ─── Tabla agrupada por fecha ───────────────────────────────
  const columnas = [
    {
      title: "Fecha",
      dataIndex: "fecha",
      key: "fecha",
      width: 130,
      render: v => dayjs(v).format(DATE_FMT),
      sorter: (a, b) => dayjs(a.fecha).valueOf() - dayjs(b.fecha).valueOf(),
      defaultSortOrder: "descend",
    },
    {
      title: "Total litros",
      dataIndex: "totalLitros",
      key: "totalLitros",
      align: "right",
      width: 130,
      render: v => (
        <Text strong style={{ color: "#1677ff" }}>
          {Number(v ?? 0).toFixed(2)} L
        </Text>
      ),
      sorter: (a, b) => Number(a.totalLitros ?? 0) - Number(b.totalLitros ?? 0),
    },
    {
      title: "Vacas ordeñadas",
      dataIndex: "cantidadVacas",
      key: "cantidadVacas",
      align: "center",
      width: 140,
      sorter: (a, b) => (a.cantidadVacas ?? 0) - (b.cantidadVacas ?? 0),
    },
    {
      title: "Promedio / vaca",
      dataIndex: "promedioPorVaca",
      key: "promedioPorVaca",
      align: "right",
      width: 140,
      render: v => `${Number(v ?? 0).toFixed(2)} L`,
      sorter: (a, b) => Number(a.promedioPorVaca ?? 0) - Number(b.promedioPorVaca ?? 0),
    },
    {
      title: "Detalle",
      key: "detalle",
      width: 100,
      align: "center",
      render: (_, record) => {
        const vacas = toArr(record.vacas);
        return (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {vacas.map(v => `${v.arete || v.animalId}: ${Number(v.litros ?? 0).toFixed(1)}L`).join(" | ") || "—"}
          </Text>
        );
      },
    },
  ];

  const expandido = (record) => {
    const vacas = toArr(record.vacas);
    if (vacas.length === 0) return <Text type="secondary">Sin detalle</Text>;

    const colsVacas = [
      {
        title: "Animal",
        key: "animal",
        render: (_, r) => (
          <span>
            <Text strong>{r.arete || "—"}</Text>
            {r.nombre ? <Text type="secondary"> — {r.nombre}</Text> : null}
          </span>
        ),
      },
      {
        title: "Litros ordeñados",
        dataIndex: "litros",
        key: "litros",
        align: "right",
        width: 160,
        render: v => (
          <Text strong style={{ color: "#1677ff", fontSize: 15 }}>
            {Number(v ?? 0).toFixed(2)} L
          </Text>
        ),
      },
      {
        title: "Observación",
        dataIndex: "observacion",
        key: "observacion",
        ellipsis: true,
        render: v => v || <Text type="secondary">—</Text>,
      },
    ];

    return (
      <Table
        rowKey="animalId"
        columns={colsVacas}
        dataSource={vacas}
        pagination={false}
        size="small"
      />
    );
  };

  // ─── Render ─────────────────────────────────────────────────
  return (
    <div style={{ padding: 16 }}>
      <Title level={3}>
        <BarChartOutlined style={{ marginRight: 8 }} />
        Estadísticas de Leche
      </Title>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text strong>Rango de fechas</Text>
              <RangePicker
                style={{ width: "100%" }}
                value={rango}
                onChange={val => val && setRango(val)}
                format={DATE_FMT}
                allowClear={false}
              />
            </Space>
          </Col>
          <Col xs={24} md={4} style={{ display: "flex", alignItems: "flex-end" }}>
            <ReloadOutlined
              onClick={cargar}
              style={{ fontSize: 20, cursor: "pointer", marginBottom: 8 }}
              spin={cargando}
            />
          </Col>
        </Row>
      </Card>

      <Spin spinning={cargando}>
        {datos.length === 0 && !cargando ? (
          <Empty description="Sin datos de leche en el rango seleccionado." />
        ) : (
          <>
            {/* ── Tarjetas resumen ── */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Text type="secondary">Total litros</Text>
                  <div style={{ fontSize: 28, fontWeight: 700 }}>
                    {totalLitros.toFixed(2)}
                  </div>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Text type="secondary">Vacas ordeñadas</Text>
                  <div style={{ fontSize: 28, fontWeight: 700 }}>
                    {totalVacasUnicas}
                  </div>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Text type="secondary">Días con registro</Text>
                  <div style={{ fontSize: 28, fontWeight: 700 }}>
                    {diasConDatos}
                  </div>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card size="small">
                  <Text type="secondary">Promedio diario</Text>
                  <div style={{ fontSize: 28, fontWeight: 700 }}>
                    {promedioDiario.toFixed(2)}
                  </div>
                </Card>
              </Col>
              {precioActual > 0 && (
                <Col xs={12} md={6}>
                  <Card size="small" style={{ borderColor: "#52c41a" }}>
                    <Text type="secondary">Ingreso estimado</Text>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "#52c41a" }}>
                      ${totalIngresos.toLocaleString("es-CO")}
                    </div>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      a ${precioActual.toLocaleString("es-CO")}/L
                    </Text>
                  </Card>
                </Col>
              )}
            </Row>

            {/* ── Gráfico de barras ── */}
            <Card title="Producción diaria" style={{ marginBottom: 16 }}>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={datosGrafico} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="fecha" />
                  <YAxis yAxisId="left" label={{ value: "Litros", angle: -90, position: "insideLeft" }} />
                  {precioActual > 0 && (
                    <YAxis yAxisId="right" orientation="right" label={{ value: "$", angle: 90, position: "insideRight" }} />
                  )}
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="totalLitros" name="Litros totales" fill="#1677ff" radius={[4, 4, 0, 0]} />
                  {precioActual > 0 && (
                    <Bar yAxisId="right" dataKey="ingresoTotal" name="Ingreso ($)" fill="#52c41a" radius={[4, 4, 0, 0]} />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </Card>

            {/* ── Tabla detallada ── */}
            <Card title="Detalle por fecha">
              <Table
                rowKey="fecha"
                columns={columnas}
                dataSource={datos}
                expandable={{ expandedRowRender: expandido }}
                pagination={{ pageSize: 15, showSizeChanger: true }}
                scroll={{ x: "max-content" }}
              />
            </Card>
          </>
        )}
      </Spin>
    </div>
  );
}
